/**
 * L6 tabs-r1 — 홈 「오늘 숫자 띠」(TodayStrip). 검색 바로 아래 · 칩 4(오늘 마감 · 접수 중 · 이번 주 공고 · 발표).
 *   · 숫자는 전부 같은 일정 rows(loadScheduleItemsAll windowDays 7)에서 센다 — 지어내는 값 0 · 0건 칩은 칸째 생략 · 전부 0 이면 띠째 생략.
 *   · 칩마다 앵커 링크(/calendar#d-{오늘} · /apt#now · /calendar#cal-week) — 서버 HTML 에 링크·숫자가 있다(모든 UA 같은 HTML).
 *   · <nav aria-label> 한 줄 · 칩 36px · 숫자 tabular-nums(home-r1.css · 접두 kd-home-). 색 추가 0(네이비·기존 토큰).
 *   · Hero 의 숫자 4(전국 현장·이번 주 청약·분양예정·미분양)는 이 띠로 흡수됐다 — Hero statsSlot 은 비운다(중복 0).
 * 순수 — 훅·DB·브라우저 API 없음.
 */
import React from 'react'; // vitest(클래식 JSX) 렌더 검증용
import { rceptOverlaps, type ScheduleEvent } from '@/lib/rx/schedule-events';

export interface TodayStripCounts {
  /** 접수 마감일이 오늘인 청약(subId 서로 다른 것). */
  closingToday: number;
  /** 접수 중(시작 ≤ 오늘 ≤ 마감). */
  open: number;
  /** 이번 주 공고 = 접수 시작이 내일~6일 뒤(오늘 포함 7일 창 · 아직 열리지 않은 것). */
  weekNotice: number;
  /** 이번 주 당첨자 발표(오늘~6일 뒤). */
  weekWinner: number;
}

type Ev = Pick<ScheduleEvent, 'kind' | 'subId' | 'rceptBgn' | 'rceptEnd' | 'dday' | 'done'>;

const addDays = (ymd: string, n: number) => new Date(Date.parse(`${ymd}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10);

/** 일정 이벤트 → 띠 숫자 4. 접수 류는 subId 로 중복 제거(한 공고 한 번). */
export function todayStripCounts(events: ReadonlyArray<Ev> | null | undefined, today: string): TodayStripCounts {
  const closing = new Set<string>();
  const open = new Set<string>();
  const notice = new Set<string>();
  const winner = new Set<string>();
  const last = addDays(today, 6);
  for (const e of events ?? []) {
    if (e.done) continue;
    if (e.kind === 'rcept') {
      const b = e.rceptBgn ?? null;
      const en = e.rceptEnd ?? b;
      if (!b || !en) continue;
      if (!rceptOverlaps(b, en, today, 6)) continue;
      const id = String(e.subId);
      if (b <= today && en >= today) {
        open.add(id);
        if (en === today) closing.add(id);
      } else if (b > today && b <= last) {
        notice.add(id);
      }
    } else if (e.kind === 'winner' && e.dday >= 0 && e.dday <= 6) {
      winner.add(String(e.subId));
    }
  }
  return { closingToday: closing.size, open: open.size, weekNotice: notice.size, weekWinner: winner.size };
}

export interface TodayStripChip { key: keyof TodayStripCounts; label: string; value: number; href: string }

/** 칩 목록(0건 생략). 순서 고정 — 오늘 마감 → 접수 중 → 이번 주 공고 → 발표. */
export function todayStripChips(c: TodayStripCounts, today: string): TodayStripChip[] {
  const all: TodayStripChip[] = [
    { key: 'closingToday', label: '오늘 마감', value: c.closingToday, href: `/calendar#d-${today}` },
    { key: 'open', label: '접수 중', value: c.open, href: '/apt#now' },
    { key: 'weekNotice', label: '이번 주 공고', value: c.weekNotice, href: '/calendar#cal-week' },
    { key: 'weekWinner', label: '발표', value: c.weekWinner, href: '/calendar#cal-week' },
  ];
  return all.filter((x) => x.value > 0);
}

export default function TodayStrip({ counts, today }: { counts: TodayStripCounts; today: string }) {
  const chips = todayStripChips(counts, today);
  if (!chips.length) return null;
  return (
    <nav className="kd-home-today" aria-label="오늘의 청약 숫자" data-cl-section="today">
      <div className="ds2-wrap kd-home-today__in">
        <ul className="kd-home-today__list">
          {chips.map((ch) => (
            <li key={ch.key}>
              <a href={ch.href} className="kd-home-today__chip" data-today-key={ch.key}>
                <span className="kd-home-today__l">{ch.label}</span>
                <b className="kd-home-today__n">{ch.value.toLocaleString('ko-KR')}</b>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}
